import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { adminCategories, Category } from '../../api/admin';
import { resolveErrorBanner, ErrorBannerState, getErrorDetail } from '../../utils/errorDetail';
import { FiPlus, FiEdit2, FiTrash2, FiFolder } from 'react-icons/fi';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: #D4A017;
  color: #1C1511;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(212, 160, 23, 0.3);
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 16px;
`;

const CategoryCard = styled.div`
  background: #F5F0E8;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const CategoryIcon = styled.div`
  width: 48px;
  height: 48px;
  background: rgba(212, 160, 23, 0.15);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #D4A017;
  font-size: 24px;
  margin-bottom: 16px;
`;

const CategoryName = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1C1511;
  margin-bottom: 8px;
`;

const CategoryDescription = styled.p`
  font-size: 14px;
  color: #6B5E4E;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const CategoryActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 8px;
  background: ${props => props.variant === 'delete' ? '#fee2e2' : 'rgba(212, 160, 23, 0.15)'};
  color: ${props => props.variant === 'delete' ? '#dc2626' : '#1C1511'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'delete' ? '#fecaca' : 'rgba(212, 160, 23, 0.3)'};
  }
`;

const Badge = styled.span<{ active: boolean }>`
  display: inline-block;
  padding: 4px 8px;
  background: ${props => props.active ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.active ? '#065f46' : '#991b1b'};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 12px;
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
  z-index: 2000;
`;

const ModalContent = styled.div`
  background: #FAF8F3;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  padding: 24px;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1C1511;
  font-family: Georgia, serif;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #6B5E4E;
  cursor: pointer;

  &:hover {
    color: #1C1511;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

// Places an EN FormGroup next to its paired ZH (Chinese) FormGroup, e.g. for
// Category Name / Category Name (Chinese). Mirrors AdminProducts.tsx's
// FormRow (task-025-s4-3).
const FormRow = styled.div`
  display: flex;
  gap: 16px;

  ${FormGroup} {
    flex: 1;
    min-width: 0;
  }
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #1C1511;
  margin-bottom: 6px;
`;

const Input = styled.input`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const TextArea = styled.textarea`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  min-height: 80px;
  resize: vertical;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid #E8DDD0;
  border-radius: 6px;
  font-size: 14px;
  background: #FFFFFF;
  color: #1C1511;

  &:focus {
    outline: none;
    border-color: #D4A017;
    box-shadow: 0 0 0 3px rgba(212, 160, 23, 0.1);
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #1C1511;
  cursor: pointer;
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: #E8DDD0;
  color: #6B5E4E;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #d9cfc4;
  }
`;

const SubmitButton = styled(Button)`
  margin: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
`;

const Spinner = styled.div`
  border: 3px solid #E8DDD0;
  border-top: 3px solid #D4A017;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6B5E4E;
`;

const ErrorMessage = styled.div`
  background: #fee2e2;
  color: #991b1b;
  border-radius: 8px;
  padding: 16px 20px;
  font-size: 14px;
  margin-bottom: 20px;
`;


const AdminCategories: React.FC = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [formData, setFormData] = useState<Category>({
    name: '',
    name_zh: '',
    description: '',
    description_zh: '',
    image_url: '',
    parent_category_id: null,
    sort_order: 0,
    active: true,
  });
  const [saveError, setSaveError] = useState<ErrorBannerState | null>(null);
  const [deleteError, setDeleteError] = useState<ErrorBannerState | null>(null);
  // Bumped on every modal-session boundary (open-add, open-edit, close) so an
  // in-flight create/update request from a now-stale session can identify
  // itself as stale in onSuccess/onError via the closure-captured sessionId,
  // and not misattribute its result to whatever session is open by the time
  // it resolves. Mirrors AdminQBMenu.tsx's sessionIdRef guard (task-025
  // Sprint 7 edge-case-auditor fix).
  const sessionIdRef = useRef(0);

  const { data: rawCategories, isLoading } = useQuery({
    queryKey: ['adminCategories'],
    queryFn: adminCategories.getAll,
  });
  const categories = Array.isArray(rawCategories) ? rawCategories : [];

  const createMutation = useMutation({
    mutationFn: ({ category }: { category: Category; sessionId: number }) =>
      adminCategories.create(category),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adminCategories'] });
      if (variables.sessionId === sessionIdRef.current) { closeModal(); }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setSaveError({ raw: getErrorDetail(err), fallbackKey: 'admin.categories.errors.addFailed' });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, category }: { id: string; category: Category; sessionId: number }) =>
      adminCategories.update(id, category),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['adminCategories'] });
      if (variables.sessionId === sessionIdRef.current) { closeModal(); }
    },
    onError: (err: any, variables) => {
      if (variables.sessionId === sessionIdRef.current) {
        setSaveError({ raw: getErrorDetail(err), fallbackKey: 'admin.categories.errors.updateFailed' });
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: adminCategories.delete,
    onSuccess: () => {
      setDeleteError(null);
      queryClient.invalidateQueries({ queryKey: ['adminCategories'] });
    },
    onError: (err: any) => {
      setDeleteError({ raw: getErrorDetail(err), fallbackKey: 'admin.categories.errors.deleteFailed' });
    },
  });

  const openModal = (category?: Category) => {
    sessionIdRef.current += 1;
    setDeleteError(null);
    if (category) {
      setEditingCategory(category);
      setFormData(category);
    } else {
      setEditingCategory(null);
      setFormData({
        name: '',
        name_zh: '',
        description: '',
        description_zh: '',
        image_url: '',
        parent_category_id: null,
        sort_order: categories.length,
        active: true,
      });
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    sessionIdRef.current += 1;
    setIsModalOpen(false);
    setEditingCategory(null);
    setSaveError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveError(null);
    const sessionId = sessionIdRef.current;
    if (editingCategory?.id) {
      updateMutation.mutate({ id: editingCategory.id, category: formData, sessionId });
    } else {
      createMutation.mutate({ category: formData, sessionId });
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('admin.categories.deleteConfirm'))) {
      setDeleteError(null);
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spinner />
      </LoadingContainer>
    );
  }

  return (
    <Container>
      <Header>
        <Title>{t('admin.categories.title')}</Title>
        <Button onClick={() => openModal()}>
          <FiPlus />
          {t('admin.categories.addButton')}
        </Button>
      </Header>

      {deleteError && (
        <p style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{resolveErrorBanner(deleteError, t)}</p>
      )}

      {categories.length === 0 ? (
        <EmptyState>
          <FiFolder size={48} />
          <h3>{t('admin.categories.emptyTitle')}</h3>
          <p>{t('admin.categories.emptyDescription')}</p>
        </EmptyState>
      ) : (
        <CategoriesGrid>
          {categories.map((category) => (
            <CategoryCard key={category.id}>
              <CategoryIcon>
                <FiFolder />
              </CategoryIcon>
              <Badge active={category.active}>
                {category.active ? t('admin.categories.statusActive') : t('admin.categories.statusInactive')}
              </Badge>
              <CategoryName>{category.name}</CategoryName>
              <CategoryDescription>
                {category.description || t('admin.categories.noDescriptionProvided')}
              </CategoryDescription>
              <CategoryActions>
                <IconButton variant="edit" onClick={() => openModal(category)}>
                  <FiEdit2 />
                </IconButton>
                <IconButton
                  variant="delete"
                  disabled={deleteMutation.isPending}
                  onClick={() => category.id && handleDelete(category.id)}
                >
                  <FiTrash2 />
                </IconButton>
              </CategoryActions>
            </CategoryCard>
          ))}
        </CategoriesGrid>
      )}

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              {editingCategory ? t('admin.categories.modalEditTitle') : t('admin.categories.modalAddTitle')}
            </ModalTitle>
            <CloseButton onClick={closeModal}>×</CloseButton>
          </ModalHeader>

          <Form onSubmit={handleSubmit}>
            <FormRow>
              <FormGroup>
                <Label>{t('admin.categories.nameLabel')}</Label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder={t('admin.categories.namePlaceholder')}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.categories.nameZhLabel')}</Label>
                <Input
                  type="text"
                  value={formData.name_zh ?? ''}
                  onChange={(e) => setFormData({ ...formData, name_zh: e.target.value })}
                />
              </FormGroup>
            </FormRow>

            <FormRow>
              <FormGroup>
                <Label>{t('admin.categories.descriptionLabel')}</Label>
                <TextArea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('admin.categories.descriptionPlaceholder')}
                />
              </FormGroup>

              <FormGroup>
                <Label>{t('admin.categories.descriptionZhLabel')}</Label>
                <TextArea
                  value={formData.description_zh ?? ''}
                  onChange={(e) => setFormData({ ...formData, description_zh: e.target.value })}
                />
              </FormGroup>
            </FormRow>

            <FormGroup>
              <Label>{t('admin.categories.parentCategoryLabel')}</Label>
              <Select
                value={formData.parent_category_id || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  parent_category_id: e.target.value || null
                })}
              >
                <option value="">{t('admin.categories.noParentOption')}</option>
                {categories
                  .filter(c => c.id !== editingCategory?.id)
                  .map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
              </Select>
            </FormGroup>

            <FormGroup>
              <Label>{t('admin.categories.sortOrderLabel')}</Label>
              <Input
                type="number"
                min="0"
                value={formData.sort_order}
                onChange={(e) => setFormData({
                  ...formData,
                  sort_order: parseInt(e.target.value) || 0
                })}
              />
            </FormGroup>

            <FormGroup>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={formData.active}
                  onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                />
                {t('admin.categories.activeCheckboxLabel')}
              </CheckboxLabel>
            </FormGroup>

            {saveError && <ErrorMessage>{resolveErrorBanner(saveError, t)}</ErrorMessage>}

            <ModalActions>
              <CancelButton type="button" onClick={closeModal}>
                {t('admin.categories.cancelButton')}
              </CancelButton>
              <SubmitButton type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                {(createMutation.isPending || updateMutation.isPending)
                  ? (editingCategory ? t('admin.categories.savingButton') : t('admin.categories.addingButton'))
                  : (editingCategory ? t('admin.categories.updateButton') : t('admin.categories.addSubmitButton'))}
              </SubmitButton>
            </ModalActions>
          </Form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AdminCategories;